(() => {
  const STORAGE_KEY = 'ombre_attribution_v1';
  const PARAM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];
  const ATTR_PREFIX = '_ombre_';
  const CART_SYNC_COOLDOWN_MS = 4500;
  const EVENT_DEDUPE_MS = 900;
  let lastCartSync = 0;
  const recentEvents = new Map();

  const routeRoot = () => window.Shopify?.routes?.root || '/';

  const safeJsonParse = (value) => {
    try {
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  };

  const readAttribution = () => safeJsonParse(window.localStorage.getItem(STORAGE_KEY)) || {};

  const writeAttribution = (payload) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // Storage can fail in private contexts; tracking should never block buying.
    }
  };

  function captureAttribution() {
    const params = new URLSearchParams(window.location.search);
    const captured = {};

    PARAM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) captured[key] = value.slice(0, 180);
    });

    const previous = readAttribution();
    const hasCampaignData = Object.keys(captured).length > 0;
    const next = {
      ...previous,
      ...captured,
      landing_page: previous.landing_page || window.location.href,
      referrer: previous.referrer || document.referrer || '',
      last_seen_page: window.location.href,
      updated_at: new Date().toISOString()
    };

    if (hasCampaignData || !previous.landing_page) writeAttribution(next);
    return next;
  }

  const cartAttributesFromAttribution = (attribution) => {
    const attributes = {};
    PARAM_KEYS.forEach((key) => {
      if (attribution[key]) attributes[`${ATTR_PREFIX}${key}`] = attribution[key];
    });
    if (attribution.landing_page) attributes[`${ATTR_PREFIX}landing_page`] = attribution.landing_page;
    if (attribution.referrer) attributes[`${ATTR_PREFIX}referrer`] = attribution.referrer;
    return attributes;
  };

  function syncCartAttribution(attribution = readAttribution()) {
    const attributes = cartAttributesFromAttribution(attribution);
    if (!Object.keys(attributes).length) return Promise.resolve();
    if (Date.now() - lastCartSync < CART_SYNC_COOLDOWN_MS) return Promise.resolve();
    lastCartSync = Date.now();

    return fetch(`${routeRoot()}cart/update.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ attributes })
    }).catch(() => {});
  }

  const normalizePrice = (value) => {
    const numeric = Number(value || 0);
    if (!Number.isFinite(numeric)) return 0;
    return numeric > 999 ? numeric / 100 : numeric;
  };

  const readProductAnalyticsJson = (source) => {
    const root = source?.closest?.('[data-ombre-native-product-detail]') || document;
    const node = root.querySelector?.('[data-ombre-product-analytics]');
    return safeJsonParse(node?.textContent) || {};
  };

  const nearestProductSource = (source) => {
    if (!source?.closest) return null;
    return source.closest('[data-product-card], [data-ombre-native-product-detail], form[action*="/cart/add"]');
  };

  const buildItemFromElement = (source, overrides = {}) => {
    const productSource = nearestProductSource(source);
    const jsonPayload = readProductAnalyticsJson(productSource || source);
    const dataset = productSource?.dataset || {};
    const variantId = overrides.variantId || source?.dataset?.variantId || dataset.selectedVariantId || dataset.variantId || jsonPayload.item_id || dataset.productId;
    const price = normalizePrice(overrides.price || dataset.productPrice || dataset.price || jsonPayload.price);
    const quantity = Math.max(1, Number(overrides.quantity || dataset.quantity || 1));

    return {
      item_id: String(overrides.item_id || jsonPayload.item_id || variantId || '').trim(),
      item_name: overrides.item_name || jsonPayload.item_name || dataset.productTitle || dataset.title || document.title,
      item_brand: overrides.item_brand || jsonPayload.item_brand || dataset.productBrand || 'Ombré Noir',
      item_category: overrides.item_category || jsonPayload.item_category || dataset.productCategory || 'Watches and bracelets',
      item_variant: overrides.item_variant || jsonPayload.item_variant || dataset.variantTitle || '',
      price,
      quantity
    };
  };

  const currencyFor = (source) => {
    const productSource = nearestProductSource(source);
    const jsonPayload = readProductAnalyticsJson(productSource || source);
    return jsonPayload.currency || productSource?.dataset?.currency || window.Shopify?.currency?.active || 'USD';
  };

  const shouldTrack = (eventName, item) => {
    const key = `${eventName}:${item.item_id}:${item.quantity}`;
    const lastTracked = recentEvents.get(key) || 0;
    if (Date.now() - lastTracked < EVENT_DEDUPE_MS) return false;
    recentEvents.set(key, Date.now());
    return true;
  };

  function pushGa4Event(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ecommerce: payload
    });

    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }
  }

  function trackAddToCart(source, overrides = {}) {
    const item = buildItemFromElement(source, overrides);
    if (!item.item_id || !shouldTrack('add_to_cart', item)) return;
    const payload = {
      currency: currencyFor(source),
      value: Number((item.price * item.quantity).toFixed(2)),
      items: [item]
    };
    pushGa4Event('add_to_cart', payload);
    syncCartAttribution();
  }

  function trackBeginCheckout(source, overrides = {}) {
    const item = buildItemFromElement(source, overrides);
    if (!shouldTrack('begin_checkout', item)) return;
    const payload = {
      currency: currencyFor(source),
      value: Number((item.price * item.quantity).toFixed(2)),
      items: [item]
    };
    pushGa4Event('begin_checkout', payload);
    syncCartAttribution();
  }

  window.OmbreAnalytics = {
    buildItemFromElement,
    captureAttribution,
    pushGa4Event,
    readAttribution,
    syncCartAttribution,
    trackAddToCart,
    trackBeginCheckout
  };

  const attribution = captureAttribution();
  syncCartAttribution(attribution);

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-add], [data-ombre-add-button], [data-ombre-sticky-button]');
    if (addButton && !addButton.disabled) trackAddToCart(addButton);

    const buyButton = event.target.closest('[data-buy]');
    if (buyButton && !buyButton.disabled) trackBeginCheckout(buyButton);

    const checkoutButton = event.target.closest('[name="checkout"], #CartDrawer-Checkout, .cart__checkout-button');
    if (checkoutButton && !checkoutButton.disabled) {
      pushGa4Event('begin_checkout', {
        currency: window.Shopify?.currency?.active || 'USD',
        items: []
      });
      syncCartAttribution();
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (form) trackAddToCart(form);
  }, true);
})();
